"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { ArrowLeft, CheckCircle, AlertCircle, Save, Loader2, Edit2, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "../../../lib/textron-data-processor";
import type { TailoringQuestion } from "../../shared/BaseTailoringQuestions";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";

interface GoingConcernSubstantiveQuestionsProps {
  onBack?: () => void;
}

interface QuestionResponse {
  selectedOption?: string;
  selectedOptions?: string[]; // For checkboxes
  textResponse?: { html: string; plainText: string };
  documents?: string[];
  lastUpdated?: string;
  companyName?: string;
}

interface QuestionState {
  selectedOption: string;
  selectedOptions: string[]; // For checkboxes
  textResponse: { html: string; plainText: string } | null;
  documents: any[];
}

// Events/conditions options
const eventsConditionsOptions = [
  // Financial
  { id: "financial-i", category: "Financial", label: "i. Net liability or net current liability position." },
  { id: "financial-ii", category: "Financial", label: "ii. Fixed-term borrowings approaching maturity without realistic prospects of renewal or repayment; or excessive reliance on short-term borrowings to finance long-term assets." },
  { id: "financial-iii", category: "Financial", label: "iii. Indications of withdrawal of financial support by creditors." },
  { id: "financial-iv", category: "Financial", label: "iv. Negative operating cash flows indicated by historical or prospective financial statements." },
  { id: "financial-v", category: "Financial", label: "v. Adverse key financial ratios." },
  { id: "financial-vi", category: "Financial", label: "vi. Substantial operating losses or significant deterioration in the value of assets used to generate cash flows." },
  // Operating
  { id: "operating-i", category: "Operating", label: "i. Management intentions to liquidate the entity or to cease operations." },
  { id: "operating-ii", category: "Operating", label: "ii. Loss of key management without replacement." },
  { id: "operating-iii", category: "Operating", label: "iii. Loss of a major market, key customer(s), franchise, license, or principal supplier(s)." },
  { id: "operating-iv", category: "Operating", label: "iv. Labor difficulties." },
  { id: "operating-v", category: "Operating", label: "v. Shortages of important supplies." },
  { id: "operating-vi", category: "Operating", label: "vi. Emergence of a highly successful competitor." },
  // Others
  { id: "others-i", category: "Others", label: "i. Non-compliance with capital or other statutory or regulatory requirements, such as solvency or liquidity requirements for financial institutions." },
  { id: "others-ii", category: "Others", label: "ii. Pending legal or regulatory proceedings against the entity that may, if successful, result in claims that the entity is unlikely to be able to satisfy." },
  { id: "others-iii", category: "Others", label: "iii. Changes in law or regulation or government policy expected to adversely affect the entity." },
  { id: "others-iv", category: "Others", label: "iv. Uninsured or underinsured catastrophes when they occur." },
];

// Create questions in TailoringQuestion format
function createQuestions(): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];

  // GC-SP-1: Events/conditions question (will be handled as custom checkbox question)
  questions.push({
    id: "GC-SP-1",
    question: "Are any of the following events/conditions identified that may cast significant doubt on the entity's ability to continue as a going concern?",
    type: "checkbox",
    options: eventsConditionsOptions.map(opt => opt.id),
  });

  // GC-SP-2: Management assessment
  questions.push({
    id: "GC-SP-2",
    question: "Has the management performed a going concern assessment?",
    type: "radio",
    options: ["Yes", "No"],
  });

  // GC-SP-2 follow-up for Yes
  questions.push({
    id: "GC-SP-2-doc-yes",
    question: "Document our procedures to discuss the assessment with management and determine whether management has identified events or conditions that, individually or collectively, may cast significant doubt on the entity's ability to continue as a going concern and, if so, management's plans to address them.",
    type: "text",
    condition: {
      questionId: "GC-SP-2",
      value: "Yes",
    },
  });

  // GC-SP-2 follow-up for No
  questions.push({
    id: "GC-SP-2-doc-no",
    question: "Document our procedures to discuss with management the basis for the intended use of the going concern basis of accounting, and inquire of management whether events or conditions exist that, individually or collectively, may cast significant doubt on the entity's ability to continue as a going concern.",
    type: "text",
    condition: {
      questionId: "GC-SP-2",
      value: "No",
    },
  });

  // GC-SP-3: Procedures to assess (shown if any event/condition is selected)
  questions.push({
    id: "GC-SP-3",
    question: "Document our procedures to be performed to assess whether or not for the events/conditions identified, a material uncertainty exists related to events or conditions that may cast significant doubt on the entity's ability to continue as a going concern, including consideration of mitigating factors.",
    type: "text",
    condition: {
      questionId: "GC-SP-1",
      value: "any", // Custom condition - any checkbox selected
    },
    guidance: {
      text: "These procedures shall include:",
      items: [
        "(a) Where management has not yet performed an assessment of the entity's ability to continue as a going concern, requesting management to make its assessment.",
        "(b) Evaluating management's plans for future actions in relation to its going concern assessment, whether the outcome of these plans is likely to improve the situation and whether management's plans are feasible in the circumstances.",
        "(c) Where the entity has prepared a cash flow forecast, and analysis of the forecast is a significant factor in considering the future outcome of events or conditions in the evaluation of management's plans for future actions:\n\ni. Evaluating the reliability of the underlying data generated to prepare the forecast; and\n\nii. Determining whether there is adequate support for the assumptions underlying the forecast.",
        "(d) Considering whether any additional facts or information have become available since the date on which management made its assessment.",
        "(e) Requesting written representations from management and, where appropriate, those charged with governance, regarding their plans for future actions and the feasibility of these plans.",
      ],
    },
  });

  // GC-SP-4: Material uncertainty conclusion
  questions.push({
    id: "GC-SP-4",
    question: "Have we concluded that a material uncertainty exists related to events/conditions that may cast a significant doubt on the entity's ability to continue as a going concern?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "GC-SP-1",
      value: "any",
    },
  });

  // GC-SP-5: Financial statements disclosure (shown if GC-SP-4 is Yes)
  questions.push({
    id: "GC-SP-5",
    question: "Do the financial statements:\n\ni. Adequately disclose the principal events or conditions that may cast significant doubt on the entity's ability to continue as a going concern and management's plans to deal with these events or conditions.\n\nii. Disclose clearly that there is a material uncertainty related to events or conditions that may cast significant doubt on the entity's ability to continue as a going concern and, therefore, that it may be unable to realize its assets and discharge its liabilities in the normal course of business.",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "GC-SP-4",
      value: "Yes",
    },
    follow_up: {
      id: "GC-SP-5-doc-no",
      question: "If No: The auditor shall:\n\n(a) Express a qualified opinion or adverse opinion, as appropriate, in accordance with SA 705; and\n\n(b) In the Basis for Qualified (Adverse) Opinion section of the auditor's report, state that a material uncertainty exists that may cast significant doubt on the entity's ability to continue as a going concern and that the financial statements do not adequately disclose this matter.",
      type: "text",
      condition: "No",
    },
  });

  // GC-SP-6: Delay in approval
  questions.push({
    id: "GC-SP-6",
    question: "Is there a significant delay in the approval of the financial statements by management or those charged with governance after the date of the financial statements?",
    type: "radio",
    options: ["Yes", "No"],
  });

  // GC-SP-6 follow-up: Is delay related to going concern?
  questions.push({
    id: "GC-SP-6-related",
    question: "Is the delay related to going concern assessment?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "GC-SP-6",
      value: "Yes",
    },
  });

  // GC-SP-6 follow-up for delay related to going concern
  questions.push({
    id: "GC-SP-6-doc",
    question: "Document additional procedures performed and consider the effect on conclusion regarding the existence of a material uncertainty.",
    type: "text",
    condition: {
      questionId: "GC-SP-6-related",
      value: "Yes",
    },
  });

  // Inference and conclusion question (always shown at the end)
  questions.push({
    id: "GC-SP-INFERENCE",
    question: "Document the inference and conclusion from the above procedure performed.",
    type: "text",
  });

  return questions;
}

export default function GoingConcernSubstantiveQuestions({ onBack }: GoingConcernSubstantiveQuestionsProps) {
  const questions = useMemo(() => createQuestions(), []);
  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sharePointSaveStatus, setSharePointSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [checkboxAnswers, setCheckboxAnswers] = useState<Record<string, string[]>>({});
  const [questionResponses, setQuestionResponses] = useState<Record<string, QuestionState>>({});
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for review comments
  const getQuestionIdForReview = (questionId: string): string => {
    return `GOING_CONCERN_${questionId}`;
  };
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

        await loadResponses(name);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const loadResponses = async (companyName: string) => {
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const res = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_GC_Substantive.json");
        if (res.success && res.data) {
          const substantiveProcedures = res.data.substantiveProcedures || {};
          
          const initialAnswers: Record<string, string> = {};
          const initialCheckboxAnswers: Record<string, string[]> = {};
          const initialResponses: Record<string, QuestionState> = {};

          questions.forEach((q) => {
            const response = substantiveProcedures[q.id] as QuestionResponse | undefined;
            if (response) {
              if (q.type === "checkbox") {
                initialCheckboxAnswers[q.id] = response.selectedOptions || [];
              } else {
                initialAnswers[q.id] = response.selectedOption || "";
              }
              initialResponses[q.id] = {
                selectedOption: response.selectedOption || "",
                selectedOptions: response.selectedOptions || [],
                textResponse: response.textResponse || null,
                documents: (response.documents || []).map((doc: string) => ({
                  id: `doc_${doc}`,
                  type: 'linked' as const,
                  name: doc
                })),
              };
            } else {
              if (q.type === "checkbox") {
                initialCheckboxAnswers[q.id] = [];
              } else {
                initialAnswers[q.id] = "";
              }
              initialResponses[q.id] = {
                selectedOption: "",
                selectedOptions: [],
                textResponse: null,
                documents: [],
              };
            }
            
            // Initialize follow-up questions
            if (q.follow_up) {
              const followUpResponse = substantiveProcedures[q.follow_up.id] as QuestionResponse | undefined;
              if (followUpResponse) {
                initialResponses[q.follow_up.id] = {
                  selectedOption: followUpResponse.selectedOption || "",
                  selectedOptions: followUpResponse.selectedOptions || [],
                  textResponse: followUpResponse.textResponse || null,
                  documents: (followUpResponse.documents || []).map((doc: string) => ({
                    id: `doc_${doc}`,
                    type: 'linked' as const,
                    name: doc
                  })),
                };
              } else {
                initialResponses[q.follow_up.id] = {
                  selectedOption: "",
                  selectedOptions: [],
                  textResponse: null,
                  documents: [],
                };
              }
            }
          });

          setAnswers(initialAnswers);
          setCheckboxAnswers(initialCheckboxAnswers);
          setQuestionResponses(initialResponses);
        } else {
          // Initialize empty state
          const initialAnswers: Record<string, string> = {};
          const initialCheckboxAnswers: Record<string, string[]> = {};
          const initialResponses: Record<string, QuestionState> = {};
          
          questions.forEach((q) => {
            if (q.type === "checkbox") {
              initialCheckboxAnswers[q.id] = [];
            } else {
              initialAnswers[q.id] = "";
            }
            initialResponses[q.id] = {
              selectedOption: "",
              selectedOptions: [],
              textResponse: null,
              documents: [],
            };
            
            // Initialize follow-up questions
            if (q.follow_up) {
              initialResponses[q.follow_up.id] = {
                selectedOption: "",
                selectedOptions: [],
                textResponse: null,
                documents: [],
              };
            }
          });
          
          setAnswers(initialAnswers);
          setCheckboxAnswers(initialCheckboxAnswers);
          setQuestionResponses(initialResponses);
        }
      }
    } catch (error) {
      console.error("Error loading responses:", error);
    }
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setDirtyQuestions(prev => new Set([...prev, questionId]));
    setIsEditMode(true);
  };

  const handleCheckboxChange = (questionId: string, optionId: string, checked: boolean) => {
    setCheckboxAnswers(prev => {
      const current = prev[questionId] || [];
      const updated = checked 
        ? [...current, optionId]
        : current.filter(id => id !== optionId);
      return { ...prev, [questionId]: updated };
    });
    setDirtyQuestions(prev => new Set([...prev, questionId]));
    setIsEditMode(true);
  };

  const handleTextResponseChange = (questionId: string, textResp: { html: string; plainText: string } | null) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse: textResp,
      },
    }));
    setDirtyQuestions(prev => new Set([...prev, questionId]));
    setIsEditMode(true);
  };

  const handleDocumentsChange = (questionId: string, docs: any[]) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents: docs,
      },
    }));
    setDirtyQuestions(prev => new Set([...prev, questionId]));
    setIsEditMode(true);
  };

  const isQuestionAnswered = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return false;
    
    if (question.type === "checkbox") {
      const selected = checkboxAnswers[questionId] || [];
      return selected.length > 0;
    }
    
    if (question.type === "radio") {
      return answers[questionId] !== undefined && answers[questionId] !== "";
    }
    
    const response = questionResponses[questionId];
    const hasTextResponse =
      !!response?.textResponse &&
      (
        (response.textResponse.html && response.textResponse.html.trim() !== "") ||
        (response.textResponse.plainText && response.textResponse.plainText.trim() !== "")
      );
    const hasDocuments =
      !!response?.documents && Array.isArray(response.documents) && response.documents.length > 0;
    const hasTable =
      !!response?.table && Array.isArray(response.table) && response.table.length > 0;

    return hasTextResponse || hasDocuments || hasTable;
  };

  const shouldShowQuestion = (question: TailoringQuestion) => {
    if (!question.condition) return true;
    
    // Handle custom "any" condition for checkboxes
    if (question.condition.value === "any") {
      const selected = checkboxAnswers[question.condition.questionId] || [];
      return selected.length > 0;
    }
    
    return answers[question.condition.questionId] === question.condition.value;
  };

  const shouldShowFollowUp = (question: TailoringQuestion) => {
    if (!question.follow_up) return false;
    if (question.follow_up.condition === "always") return true;
    return answers[question.id] === question.follow_up.condition;
  };

  // Helper function to sanitize data for JSON serialization
  const sanitizeForJSON = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForJSON(item));
    }
    
    // Handle Date objects
    if (obj instanceof Date) {
      return obj.toISOString();
    }
    
    // Create a new object with only serializable properties
    const sanitized: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        // Skip functions, symbols, and other non-serializable types
        if (typeof value === 'function' || typeof value === 'symbol') {
          continue;
        }
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeForJSON(value);
      }
    }
    return sanitized;
  };

  const saveResponses = useCallback(async () => {
    if (!companyName) return;

    try {
      // Trigger auto-save for all DocumentManager instances
      for (const question of questions) {
        if (question.type === "text" || question.follow_up) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_ConcludingProcedures_GC_${question.id}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
          if (question.follow_up) {
            const followUpAutoSave = (window as any)[`autoSaveTextbox_ConcludingProcedures_GC_${question.follow_up.id}`];
            if (followUpAutoSave && typeof followUpAutoSave === 'function') {
              followUpAutoSave();
            }
          }
        }
      }
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSharePointSaveStatus('saving');
      setSaveError(null);

      // Load existing file
      let existingData: any = {};
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const res = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_GC_Substantive.json");
        if (res.success && res.data) {
          existingData = res.data;
        }
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const substantiveProcedures: Record<string, QuestionResponse> = {};

      // Save all questions
      for (const q of questions) {
        const state = questionResponses[q.id] || { selectedOption: "", selectedOptions: [], textResponse: null, documents: [] };
        const textboxFromLocalStorage = localStorage.getItem(`textbox_ConcludingProcedures_GC_${q.id}`);
        const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

        // For inference question, query db.json for section field like CustomProcedure
        let documentNames: string[] = [];
        if (q.id === "GC-SP-INFERENCE") {
          const sectionToFind = `ConcludingProcedures_GC_${q.id}`;
          try {
            if ((window as any).sharePointAPI?.loadDocumentListBySection) {
              const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
              if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
                documentNames = dbResult.data
                  .map((file: any) => file.name || '')
                  .filter((name: string) => name && name.trim().length > 0);
              }
            }
          } catch (error) {
            console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
          }
        } else {
          const fileDocuments = (state.documents || []).filter((d: any) => d.type !== 'textbox');
          documentNames = fileDocuments.map((d: any) => {
            // Ensure we only extract string names, not objects
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object' && d.name) return d.name;
            return String(d);
          });
        }

        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (state.textResponse) {
          textResponse = state.textResponse;
        }

        if (q.type === "checkbox") {
          substantiveProcedures[q.id] = {
            selectedOptions: checkboxAnswers[q.id] || [],
            textResponse: textResponse,
            documents: documentNames,
            lastUpdated: new Date().toISOString(),
            companyName: companyName || "",
          };
        } else {
          substantiveProcedures[q.id] = {
            selectedOption: answers[q.id] || "",
            textResponse: textResponse,
            documents: documentNames,
            lastUpdated: new Date().toISOString(),
            companyName: companyName || "",
          };
        }
      }

      // Merge into existing data
      Object.entries(substantiveProcedures).forEach(([qId, value]) => {
        existingData.substantiveProcedures[qId] = value;
      });

      // Sanitize the data before stringifying to remove any non-serializable properties
      const sanitizedData = sanitizeForJSON(existingData);
      const jsonString = JSON.stringify(sanitizedData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: "ConcludingProcedures_GC_Substantive.json",
        referenceValue: "Concluding Procedures Going Concern Substantive Responses",
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        sectionList: ["ConcludingProcedures_GC_Substantive"],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSharePointSaveStatus('saved');
      setDirtyQuestions(new Set());
      setIsEditMode(false);
    } catch (error: any) {
      console.error("Error saving responses:", error);
      setSharePointSaveStatus('error');
      setSaveError(error?.message || "Failed to save responses");
    }
  }, [companyName, questions, answers, checkboxAnswers, questionResponses, dirtyQuestions]);

  const handleComplete = async () => {
    if (isEditMode) {
      try {
        setIsSaving(true);
        await saveResponses();
      } catch (error) {
        console.error("Error completing:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const renderQuestion = (question: TailoringQuestion, isFollowUp = false) => {
    const isAnswered = isQuestionAnswered(question.id);
    
    // Don't render if condition not met
    if (!shouldShowQuestion(question)) {
      return null;
    }
    
    return (
      <Card key={question.id} className={`border-white/10 bg-white/5 text-white ${isFollowUp ? 'ml-6 border-l-2 border-l-[#4da3ff]/30' : ''}`}>
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="flex-shrink-0">
                  {isAnswered ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-white/40" />
                  )}
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium text-white whitespace-pre-line">
                    {question.question}
                  </Label>
                  {question.guidance && (
                  <div className="mt-2 p-3 bg-white/5 border border-white/10 rounded-md">
                    <div className="text-xs font-semibold text-white/90 mb-2">{question.guidance.text}</div>
                    <ul className="list-decimal list-inside space-y-1 text-xs text-white/80">
                      {question.guidance.items.map((item, idx) => (
                        <li key={idx} className="whitespace-pre-line">{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getQuestionIdForReview(question.id))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>

            {question.type === "checkbox" && question.options && (
              <div className="ml-8 space-y-4">
                {["Financial", "Operating", "Others"].map((category) => {
                  const categoryOptions = eventsConditionsOptions.filter(opt => opt.category === category);
                  if (categoryOptions.length === 0) return null;
                  
                  return (
                    <div key={category} className="space-y-2">
                      <div className="text-sm font-semibold text-white/90">{category}:</div>
                      <div className="space-y-2 ml-4">
                        {categoryOptions.map((opt) => {
                          const isChecked = (checkboxAnswers[question.id] || []).includes(opt.id);
                          return (
                            <div key={opt.id} className="flex items-start space-x-2">
                              <Checkbox
                                id={`${question.id}-${opt.id}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => handleCheckboxChange(question.id, opt.id, checked === true)}
                                disabled={!isEditMode}
                                className="border-white/20 data-[state=checked]:bg-[#4da3ff] data-[state=checked]:border-[#4da3ff] mt-1"
                              />
                              <Label
                                htmlFor={`${question.id}-${opt.id}`}
                                className="text-sm text-white/80 cursor-pointer whitespace-pre-line flex-1"
                              >
                                {opt.label}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {question.type === "radio" && question.options && (
              <>
                <RadioGroup
                  value={answers[question.id] || ""}
                  onValueChange={(value) => handleAnswerChange(question.id, value)}
                  disabled={!isEditMode}
                  className="ml-8"
                >
                  {question.options.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <RadioGroupItem 
                        value={option} 
                        id={`${question.id}-${option}`}
                        className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                      />
                      <Label 
                        htmlFor={`${question.id}-${option}`}
                        className="text-sm text-white/80 cursor-pointer whitespace-pre-line"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                
                {/* Show "If Yes" guidance box when Yes is selected for GC-SP-5 */}
                {question.id === "GC-SP-5" && answers[question.id] === "Yes" && (
                  <div className="ml-8 mt-3 p-3 bg-white/5 border border-white/10 rounded-md">
                    <div className="text-xs font-semibold text-white/90 mb-2">If Yes:</div>
                    <div className="text-xs text-white/80 whitespace-pre-line">
                      The auditor shall express an unmodified opinion and the auditor's report shall include a separate section under the heading "Material Uncertainty Related to Going Concern" to:
                      {"\n\n"}(a) Draw attention to the note in the financial statements that discloses the matters set out in paragraph 19; and
                      {"\n\n"}(b) State that these events or conditions indicate that a material uncertainty exists that may cast significant doubt on the entity's ability to continue as a going concern and that the auditor's opinion is not modified in respect of the matter.
                    </div>
                  </div>
                )}
                
                {/* Show "If No" guidance box when No is selected for GC-SP-5 */}
                {question.id === "GC-SP-5" && answers[question.id] === "No" && question.follow_up && (
                  <div className="ml-8 mt-3 p-3 bg-white/5 border border-white/10 rounded-md">
                    <div className="text-xs font-semibold text-white/90 mb-2">If No: The auditor shall:</div>
                    <div className="text-xs text-white/80 whitespace-pre-line">
                      (a) Express a qualified opinion or adverse opinion, as appropriate, in accordance with SA 705; and
                      {"\n\n"}(b) In the Basis for Qualified (Adverse) Opinion section of the auditor's report, state that a material uncertainty exists that may cast significant doubt on the entity's ability to continue as a going concern and that the financial statements do not adequately disclose this matter.
                    </div>
                  </div>
                )}
              </>
            )}

            {(question.type === "document" || question.type === "text") && (
              <div className="ml-8 mt-3">
                <DocumentManager
                  sectionKey={`ConcludingProcedures_GC_${question.id}`}
                  itemId={question.id}
                  itemQuestion={question.question}
                  itemTextResponse={questionResponses[question.id]?.textResponse}
                  itemDocuments={questionResponses[question.id]?.documents?.map((doc: string | any) => {
                    if (typeof doc === 'string') {
                      return {
                        id: `doc_${doc}`,
                        type: 'linked' as const,
                        name: doc
                      };
                    }
                    return doc;
                  }) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange(question.id, docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange(question.id, textResp)}
                  disabled={!isEditMode}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Going Concern - Substantive Procedures</h2>
            <p className="text-gray-400">Perform detailed procedures over Going Concern</p>
          </div>
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Going Concern - Substantive Procedures</h2>
          <p className="text-gray-400">Perform detailed procedures over Going Concern</p>
        </div>
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
          {isEditMode ? (
            <Button
              onClick={handleComplete}
              disabled={isSaving || sharePointSaveStatus === 'saving'}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSaving || sharePointSaveStatus === 'saving' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" /> Save
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => setIsEditMode(true)}
              variant="outline"
            >
              <Edit2 className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
        </div>
      </div>

      {saveError && (
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">{saveError}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {sharePointSaveStatus === 'saved' && (
        <Card className="border-green-500/20 bg-green-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-300">
              <CheckCircle className="h-5 w-5" />
              <p className="text-sm">Responses saved successfully</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {questions.map((question) => {
          const rendered = renderQuestion(question);
          if (!rendered) return null;
          
          return (
            <div key={question.id}>
              {rendered}
              {/* Render follow-up as separate question if it's not inline */}
              {question.follow_up && shouldShowFollowUp(question) && question.follow_up.type !== "text" && question.follow_up.type !== "document" && (
                <div className="mt-4">
                  {renderQuestion({
                    id: question.follow_up.id,
                    question: question.follow_up.question,
                    type: question.follow_up.type,
                    options: question.follow_up.options,
                  } as TailoringQuestion, true)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to question text for display
            const questionMap: Record<string, string> = {};
            questions.forEach((q) => {
              questionMap[getQuestionIdForReview(q.id)] = q.question;
            });
            return questionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

